import { useMemo, useState, useEffect, useRef } from 'react';

export default function useSearchable(
  options: any[], 
  searchable: boolean, 
  onSearch?: (searchTerm: string) => void
) {
  const [keywords, setKeywords] = useState('');
  const isFirstRender = useRef(true);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Call onSearch callback when keywords change (for server-side search)
  useEffect(() => {
    // Skip the initial render to avoid calling onSearch on mount
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (onSearch && searchable) {
      // Clear previous timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // Debounce the search to avoid too many API calls
      debounceTimer.current = setTimeout(() => {
        onSearch(keywords);
      }, 300); // 300ms debounce
    }

    // Cleanup timer on unmount
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [keywords, onSearch, searchable]);

  const filteredOptions = useMemo<any[]>(() => {
    // If onSearch is provided, don't do client-side filtering
    // (options will be filtered by server)
    if (onSearch) return options;
    
    if (!Array.isArray(options) || !options.length) return [];
    if (!searchable) return options;
    if (!keywords?.trim()) return options;
    const keywordsLowerCase = keywords?.toLowerCase() || '';
    // eslint-disable-next-line arrow-body-style
    return options.filter((option) => {
      // Search in both text and text2 fields
      const textMatch = !!option?.text?.toLowerCase()?.includes(keywordsLowerCase);
      const text2Match = !!option?.text2?.toLowerCase()?.includes(keywordsLowerCase);
      return textMatch || text2Match;
    });
  }, [searchable, options, keywords, onSearch]);

  const handleKeywordsChange = (payload?: string) => {
    if (!searchable) return;
    setKeywords(payload || '');
  };

  const clearKeywords = () => {
    setKeywords('');
  };

  return { keywords, filteredOptions, onKeywordsChange: handleKeywordsChange, clearKeywords };
}
